export function initials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join("");
}

// Currency defaults — overridable at runtime via `setCurrency()`. This lets
// `fmtMoney(n)` stay callable everywhere without threading a currency prop or
// hook through hundreds of components. BillingSettingsContext calls
// setCurrency() when it loads /api/payments/settings/billing.
let _currencySymbol = "₹";
let _currencyCode = "INR";

// Workspace time zone — set from the branch's `timezone` setting
// (Settings → Branches) when the app boots. Every date/time the UI
// renders goes through the helpers below, so one value changes the whole
// app's wall clock. Defaults to IST.
let _timezone = "Asia/Kolkata";

export function setTimezone(tz) {
  if (tz) _timezone = tz;
}

export function getTimezone() {
  return _timezone;
}

// Short abbreviation for labels and column headers, e.g. "IST".
// Chromium's ICU returns "GMT+5:30" for Asia/Kolkata, so known zones are
// mapped explicitly to keep the UI in step with the backend's exports.
const TZ_ABBREV = {
  "Asia/Kolkata": "IST",
  "Asia/Calcutta": "IST",
  "Asia/Colombo": "IST",
  "Asia/Kathmandu": "NPT",
  "Asia/Karachi": "PKT",
  "Asia/Dhaka": "BST",
  "Asia/Dubai": "GST",
};

export function timezoneLabel() {
  if (TZ_ABBREV[_timezone]) return TZ_ABBREV[_timezone];
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: _timezone, timeZoneName: "short",
    }).formatToParts(new Date());
    return parts.find(p => p.type === "timeZoneName")?.value || _timezone;
  } catch { return _timezone; }
}

export function setCurrency(symbol, code) {
  if (symbol) _currencySymbol = symbol;
  if (code) _currencyCode = code;
}

export function getCurrency() {
  return { symbol: _currencySymbol, code: _currencyCode };
}

// INR/PKR/BDT/LKR use the Indian numbering system (1,00,000). Everyone else
// gets Western grouping (100,000). This matches how the PDF and dashboards
// should look.
const IN_LOCALE_CODES = new Set(["INR", "PKR", "BDT", "LKR", "NPR"]);

export function fmtMoney(n = 0) {
  const locale = IN_LOCALE_CODES.has(_currencyCode) ? "en-IN" : "en-US";
  return `${_currencySymbol}${Number(n).toLocaleString(locale, { maximumFractionDigits: 2 })}`;
}

export function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { timeZone: _timezone, day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

export function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { timeZone: _timezone, day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", { timeZone: _timezone, hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

// Full timestamp with seconds — audit trails, check-in/out logs.
export function fmtDateTimeFull(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: _timezone, day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
}

// Date rendered with custom Intl options, always on the workspace clock.
export function fmtDateOpts(iso, opts = {}) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { timeZone: _timezone, ...opts });
  } catch { return iso; }
}

export function fmtTimeOpts(iso, opts = {}) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", { timeZone: _timezone, ...opts });
  } catch { return iso; }
}

// "Today" on the workspace clock, as YYYY-MM-DD.
export function todayLocalISO() {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: _timezone, year: "numeric", month: "2-digit", day: "2-digit",
    }).format(new Date());
  } catch { return new Date().toISOString().slice(0, 10); }
}

export function waitingTime(iso, baseSeconds = 0) {
  const base = Math.max(0, Math.floor(baseSeconds || 0));
  if (!iso) return base ? fmtWait(Math.floor(base / 60)) : "0m";
  const elapsed = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  return fmtWait(Math.floor((base + Math.max(0, elapsed)) / 60));
}

function fmtWait(mins) {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
}

export function ageFromDob(dob) {
  if (!dob) return "—";
  const d = new Date(dob);
  const diff = Date.now() - d.getTime();
  const years = Math.floor(diff / (365.25 * 24 * 3600 * 1000));
  return `${years}y`;
}

export const ROLE_LABELS = {
  owner: "Owner",
  admin: "Admin",
  reception: "Reception",
  therapist: "Therapist",
  guardian: "Parent / Guardian",
  accountant: "Accountant",
  devtester: "DevTester",
  // iter-57 — additive QR roles. A user can hold these alongside any
  // other role; they gate the top-bar Display QR / Scan QR buttons.
  display_qr: "Display QR",
  scan_qr: "Scan QR",
};

// iter-57 — Roles that are "additive" — they don't grant page access
// on their own, only the QR top-bar buttons. Used in the Users role
// picker to render them in a separate helper group.
export const ADDITIVE_QR_ROLES = ["display_qr", "scan_qr"];

export const PRIORITY_STYLES = {
  urgent: "bg-red-100 text-red-700 border-red-200",
  high: "bg-amber-100 text-amber-800 border-amber-200",
  normal: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

export const SESSION_STATUS_STYLES = {
  scheduled: "bg-blue-50 text-blue-700 border-blue-100",
  in_progress: "bg-teal-100 text-teal-800 border-teal-200",
  paused: "bg-amber-100 text-amber-800 border-amber-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

/**
 * Build a disambiguated label for a student in a picker. Falls back to the
 * bare name if no guardians are recorded. Always includes the primary
 * guardian's name so two "Aarav Sharma"s don't look identical in a dropdown.
 */
export function studentLabel(s) {
  if (!s) return "";
  const sid = s.student_id ? `${s.student_id} · ` : "";
  const guardians = (s.guardians || []).map(g => g.name).filter(Boolean);
  if (guardians.length === 0) return `${sid}${s.name}`;
  const first = guardians[0];
  const more = guardians.length > 1 ? ` +${guardians.length - 1}` : "";
  return `${sid}${s.name} — ${first}${more}`;
}

/**
 * Build a disambiguated label for a user (therapist/reception/etc.). Same
 * problem — two staffers with the same name shouldn't be indistinguishable
 * in a picker. Email is the most unique attribute available client-side.
 */
export function userLabel(u) {
  if (!u) return "";
  if (u.email) return `${u.name} · ${u.email}`;
  if (u.phone) return `${u.name} · ${u.phone}`;
  return u.name;
}
